import { useState, useEffect } from 'react'
import axios from 'axios'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { 
  Users, Mail, Send, X, Clock, CheckCircle2, 
  UserMinus, Loader2, AlertCircle
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export function ShareTaskModal({ task, open, onOpenChange, onShareUpdate }) {
  const { t, locale } = useLanguage()
  const [email, setEmail] = useState('')
  const [shares, setShares] = useState([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [revoking, setRevoking] = useState(null)

  useEffect(() => {
    if (open && task) {
      fetchShares()
    }
  }, [open, task])

  const fetchShares = async () => {
    if (!task) return
    setLoading(true)
    try {
      const response = await axios.get(`${API}/tasks/${task.id}/shares`)
      setShares(response.data)
    } catch (error) {
      console.error('Error fetching shares:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleShare = async (e) => {
    e.preventDefault()
    if (!email.trim()) {
      toast.error(locale === 'es-MX' ? 'Ingresa un email' : 'Enter an email')
      return
    }

    setSending(true)
    try {
      await axios.post(`${API}/tasks/${task.id}/share`, { email: email.trim() })
      toast.success(locale === 'es-MX' ? 'Invitación enviada' : 'Invitation sent')
      setEmail('')
      fetchShares()
      onShareUpdate?.()
    } catch (error) {
      const message = error.response?.data?.detail || 
        (locale === 'es-MX' ? 'Error al compartir' : 'Error sharing')
      toast.error(message)
    } finally {
      setSending(false)
    }
  }

  const handleRevoke = async (shareId) => {
    setRevoking(shareId)
    try {
      await axios.post(`${API}/task-shares/${shareId}/revoke`)
      toast.success(locale === 'es-MX' ? 'Acceso revocado' : 'Access revoked')
      fetchShares()
      onShareUpdate?.()
    } catch (error) {
      toast.error(locale === 'es-MX' ? 'Error al revocar' : 'Error revoking')
    } finally {
      setRevoking(null)
    }
  }

  const getStatusConfig = (status) => {
    if (status === 'pending_invite') {
      return {
        icon: Clock,
        label: locale === 'es-MX' ? 'Pendiente' : 'Pending',
        color: 'bg-amber-500/20 text-amber-600'
      }
    }
    return {
      icon: CheckCircle2,
      label: locale === 'es-MX' ? 'Activo' : 'Active',
      color: 'bg-emerald-500/20 text-emerald-600'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            {locale === 'es-MX' ? 'Compartir tarea' : 'Share task'}
          </DialogTitle>
        </DialogHeader>

        {task && (
          <div className="space-y-4">
            {/* Task info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="font-medium text-sm">{task.title}</p>
              {task.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {task.description}
                </p>
              )}
            </div>

            {/* Share form */}
            <form onSubmit={handleShare} className="space-y-3">
              <div>
                <Label htmlFor="share-email">
                  {locale === 'es-MX' ? 'Email del colaborador' : 'Collaborator email'}
                </Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="share-email"
                      type="email"
                      placeholder="email@ejemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="share-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending || !email.trim()}
                    data-testid="send-invite-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {locale === 'es-MX' 
                    ? 'El colaborador solo podrá cambiar el estado de la tarea' 
                    : 'Collaborator can only change task status'}
                </p>
              </div>
            </form>

            {/* Shares list */}
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                {locale === 'es-MX' ? 'Compartido con' : 'Shared with'}
              </Label>
              
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : shares.length === 0 ? (
                <div className="text-center py-4">
                  <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {locale === 'es-MX' ? 'Aún no compartes esta tarea' : 'Not shared yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 mt-2 max-h-48 overflow-y-auto">
                  {shares.map((share) => {
                    const statusConfig = getStatusConfig(share.status)
                    const StatusIcon = statusConfig.icon
                    
                    return (
                      <div 
                        key={share.id}
                        className="flex items-center justify-between p-2 bg-muted/30 rounded-lg"
                        data-testid={`share-item-${share.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {share.shared_with_name || share.shared_with_email}
                            </p>
                            <div className="flex items-center gap-1.5">
                              <Badge variant="secondary" className={`text-xs ${statusConfig.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRevoke(share.id)}
                          disabled={revoking === share.id}
                          className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                          data-testid={`revoke-btn-${share.id}`}
                        >
                          {revoking === share.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserMinus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {locale === 'es-MX' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
